import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { TrendingUp, Clock, CheckCircle2, Calendar, BarChart3 } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo, tabAccessibleFor } from "../lib/modules";
import SalesAdmin from "./SalesAdmin";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const fmtINR = (n) => "₹ " + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

function buildFYMonths(fyStartIso) {
  // fyStartIso = YYYY-04-01...
  const s = new Date(fyStartIso);
  const startYear = s.getUTCFullYear();
  const keys = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(startYear, 3 + i, 1));
    keys.push({ key: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`, label: MONTHS[i] });
  }
  return keys;
}

export default function MySales() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const canSalesAdmin = isMaster
    || tabAccessibleFor(user, "my_sales", "sales_admin")
    || moduleCanDo(user, "my_sales", "edit");
  const canMySales = tabAccessibleFor(user, "my_sales", "my_sales") || true; // every user sees their own
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const initialTab = requestedTab === "sales_admin" && canSalesAdmin ? "sales_admin" : "my_sales";
  const [outerTab, setOuterTab] = useState(initialTab);

  useEffect(() => {
    // Keep URL in sync so /my-sales?tab=sales_admin deep-links work and
    // the redirected /sales-admin route lands on the correct sub-tab.
    if (outerTab === "sales_admin") setSearchParams({ tab: "sales_admin" }, { replace: true });
    else if (searchParams.get("tab")) setSearchParams({}, { replace: true });
  }, [outerTab, searchParams, setSearchParams]);

  return (
    <div className="max-w-[1500px] space-y-6">
      {canSalesAdmin ? (
        <Tabs value={outerTab} onValueChange={setOuterTab}>
          <TabsList data-testid="my-sales-outer-tabs">
            {canMySales && (
              <TabsTrigger value="my_sales" data-testid="tab-my-sales">
                <TrendingUp size={12} className="mr-1.5" /> My Sales
              </TabsTrigger>
            )}
            <TabsTrigger value="sales_admin" data-testid="tab-sales-admin">
              <BarChart3 size={12} className="mr-1.5" /> Sales Admin
            </TabsTrigger>
          </TabsList>
          {canMySales && (
            <TabsContent value="my_sales" className="mt-4">
              <MySalesPanel />
            </TabsContent>
          )}
          <TabsContent value="sales_admin" className="mt-4">
            <SalesAdmin embedded />
          </TabsContent>
        </Tabs>
      ) : (
        <MySalesPanel />
      )}
    </div>
  );
}

function MySalesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fy, setFy] = useState(null);

  useEffect(() => {
    setLoading(true);
    const url = fy ? `/my-sales?fy=${fy}` : "/my-sales";
    api.get(url).then(({ data }) => setData(data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [fy]);

  const monthly = data?.monthly || {};
  const months = data ? buildFYMonths(data.fy_start) : [];
  const maxApproved = useMemo(() => Math.max(1, ...months.map((m) => (monthly[m.key]?.approved || 0))), [monthly, months]);

  const fyStartYear = data ? new Date(data.fy_start).getUTCFullYear() : new Date().getUTCFullYear();
  const fyOptions = [];
  for (let y = fyStartYear + 1; y >= fyStartYear - 3; y--) fyOptions.push(y);

  return (
    <div className="space-y-6">
      <PageHeader
        moduleKey="sales_orders"
        tone="emerald"
        icon={TrendingUp}
        eyebrow="Sales Performance"
        title="My Sales"
        subtitle="Your share of every order tagged to you. Indian Financial Year — April to March."
        actions={
          <select value={fy ?? fyStartYear} onChange={(e) => setFy(parseInt(e.target.value, 10))} className="h-10 px-3 border border-emerald-200 bg-white/80 rounded-lg text-sm font-semibold text-emerald-700" data-testid="my-sales-fy">
            {fyOptions.map((y) => <option key={y} value={y}>FY {String(y).slice(-2)}-{String(y + 1).slice(-2)}</option>)}
          </select>
        }
      />

      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={<TrendingUp size={16} />}
              label={`This FY (${data.fy_label}) — Approved`}
              value={fmtINR(data.fy_total_approved)}
              tone="emerald"
              testid="stat-fy-approved"
            />
            <StatCard
              icon={<Clock size={16} />}
              label={`This FY — Pending`}
              value={fmtINR(data.fy_total_pending)}
              tone="amber"
              testid="stat-fy-pending"
            />
            <StatCard
              icon={<Calendar size={16} />}
              label="This Month — Approved"
              value={fmtINR(data.this_month_approved)}
              tone="emerald"
              testid="stat-month-approved"
            />
            <StatCard
              icon={<Clock size={16} />}
              label="This Month — Pending"
              value={fmtINR(data.this_month_pending)}
              tone="amber"
              testid="stat-month-pending"
            />
          </div>

          {/* Monthly chart */}
          <div className="pk-card p-5" data-testid="my-sales-monthly-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Monthly Breakdown</div>
                <div className="text-sm text-slate-500 mt-0.5">{data.fy_label} · Apr → Mar</div>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-1.5 items-end h-40">
              {months.map((m) => {
                const a = monthly[m.key]?.approved || 0;
                const p = monthly[m.key]?.pending || 0;
                const hA = (a / maxApproved) * 100;
                const hP = (p / maxApproved) * 100;
                const isOverride = monthly[m.key]?.override;
                return (
                  <div key={m.key} className="flex flex-col items-center justify-end gap-1" data-testid={`bar-${m.key}`}>
                    <div className="text-[9px] mono text-slate-500 h-3">{a > 0 ? Math.round(a / 1000) + "k" : ""}</div>
                    <div className="w-full flex flex-col items-stretch gap-0.5">
                      {p > 0 && <div className="w-full bg-amber-300 rounded-sm" style={{ height: `${Math.max(2, hP)}%` }} title={`Pending ${fmtINR(p)}`}></div>}
                      <div className={`w-full ${isOverride ? "bg-blue-500" : "bg-emerald-500"} rounded-sm`} style={{ height: `${Math.max(a > 0 ? 4 : 0, hA)}%` }} title={`${isOverride ? "Override " : "Approved "}${fmtINR(a)}`}></div>
                    </div>
                    <div className="text-[10px] font-semibold text-slate-700">{m.label}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"></span> Approved</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-300 inline-block"></span> Pending</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block"></span> Master override</span>
            </div>
          </div>

          {/* Orders Tabs */}
          <Tabs defaultValue="approved">
            <TabsList>
              <TabsTrigger value="approved" data-testid="ms-tab-approved"><CheckCircle2 size={12} className="mr-1.5"/> Approved ({data.approved_orders.length})</TabsTrigger>
              <TabsTrigger value="pending" data-testid="ms-tab-pending"><Clock size={12} className="mr-1.5"/> Pending Approval ({data.pending_orders.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="approved" className="mt-3">
              <OrdersTable rows={data.approved_orders} pending={false} />
            </TabsContent>
            <TabsContent value="pending" className="mt-3">
              <OrdersTable rows={data.pending_orders} pending={true} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone = "emerald", testid }) {
  const toneCls = tone === "emerald" ? "border-emerald-200 bg-emerald-50/40 text-emerald-700" : "border-amber-200 bg-amber-50/40 text-amber-700";
  return (
    <div className={`pk-card p-4 border ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold">{icon} {label}</div>
      <div className="text-[26px] font-extrabold text-slate-900 mt-1.5 mono">{value}</div>
    </div>
  );
}

function OrdersTable({ rows, pending }) {
  const [viewMode] = useGlobalViewMode();
  if (!rows || rows.length === 0) {
    return <div className="pk-card p-12 text-center text-slate-400 text-sm">No orders here yet.</div>;
  }
  if (viewMode === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ms-cards">
        {rows.map((r) => (
          <div key={r.order_id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`ms-card-${r.order_no}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="mono font-bold text-emerald-700 text-[12.5px] truncate">{r.order_no}</div>
                <div className="font-bold text-slate-800 truncate mt-0.5">{r.client_name}</div>
              </div>
              {pending
                ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] shrink-0">Pending</Badge>
                : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] shrink-0">Approved</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-[12px]">
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Total Value</div>
                <div className="mono font-semibold text-slate-800">₹ {r.total_value}</div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">My Share</div>
                <div className="mono font-bold text-emerald-700">{fmtINR(r.share_amount)} <span className="text-[10px] text-slate-500 font-medium">({r.share_percent}%)</span></div>
              </div>
              <div className="col-span-2">
                <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Created</div>
                <div className="text-[11px] text-slate-600 mono">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <th className="px-4 py-3 text-left pk-table-header">Order No</th>
            <th className="px-4 py-3 text-left pk-table-header">Client</th>
            <th className="px-4 py-3 text-left pk-table-header">Total Value</th>
            <th className="px-4 py-3 text-left pk-table-header">My Share</th>
            <th className="px-4 py-3 text-left pk-table-header">Created</th>
            <th className="px-4 py-3 text-left pk-table-header">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.order_id} className="border-b border-slate-100" data-testid={`ms-row-${r.order_no}`}>
              <td className="px-4 py-3 mono font-semibold text-slate-900" data-label="Order">{r.order_no}</td>
              <td className="px-4 py-3" data-label="Client">{r.client_name}</td>
              <td className="px-4 py-3 mono" data-label="Total Value">₹ {r.total_value}</td>
              <td className="px-4 py-3 mono" data-label="My Share">
                <span className="font-semibold">{fmtINR(r.share_amount)}</span>
                <span className="text-[10px] text-slate-500 ml-1.5">({r.share_percent}%)</span>
              </td>
              <td className="px-4 py-3 text-[11px] text-slate-600" data-label="Created">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-3" data-label="Status">
                {pending
                  ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Pending</Badge>
                  : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Approved</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
